import { TFunction } from 'i18next';
import moment from 'moment-timezone';
import { LoyaltyOfferDTO, OfferRedeemSlotsDTO, OfferSlotDay } from './type';

export const OFFER_SLOT_DAYS: OfferSlotDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const inWeekOrder = (days: OfferSlotDay[]): OfferSlotDay[] => OFFER_SLOT_DAYS.filter(day => days.includes(day));

export type OfferSlotState = 'open' | 'closed' | 'ended';

// Computed fields are only set on offers that carry slots, so null means "no slots".
export const offerSlotState = (
  offer: Pick<LoyaltyOfferDTO, 'redeemSlotOpen' | 'redeemSlotsEnded'>,
): OfferSlotState | null => {
  if (offer.redeemSlotsEnded === true) return 'ended';
  if (offer.redeemSlotOpen === true) return 'open';
  if (offer.redeemSlotOpen === false) return 'closed';
  return null;
};

// Bounds arrive on the franchise clock, so the offset they carry is the one to display.
export const formatSlotInstant = (iso: string): string => {
  const instant = moment.parseZone(iso);
  const isToday = instant.isSame(moment().utcOffset(instant.utcOffset()), 'day');
  return instant.format(isToday ? 'HH:mm' : 'ddd D MMM HH:mm');
};

export const offerSlotLabel = (
  offer: Pick<LoyaltyOfferDTO, 'redeemSlotOpen' | 'redeemSlotsEnded' | 'slotClosesAt' | 'nextSlotOpensAt'>,
  t: TFunction,
): string | null => {
  switch (offerSlotState(offer)) {
    case 'open':
      return offer.slotClosesAt
        ? t('loyalty.redeemSlots.state.openUntil', { ns: 'loyalty', time: formatSlotInstant(offer.slotClosesAt) })
        : t('loyalty.redeemSlots.state.open', { ns: 'loyalty' });
    case 'closed':
      return offer.nextSlotOpensAt
        ? t('loyalty.redeemSlots.state.next', { ns: 'loyalty', time: formatSlotInstant(offer.nextSlotOpensAt) })
        : t('loyalty.redeemSlots.state.closed', { ns: 'loyalty' });
    case 'ended':
      return t('loyalty.redeemSlots.state.ended', { ns: 'loyalty' });
    default:
      return null;
  }
};

export const offerSlotsSummary = (slots: OfferRedeemSlotsDTO, t: TFunction): string => {
  const weekly = slots.weekly.map(group => {
    const days = inWeekOrder(group.days)
      .map(day => t(`basic.days.${day}`).substring(0, 3))
      .join(', ');
    return `${days} ${group.hours.map(hours => `${hours.start}–${hours.end}`).join(', ')}`;
  });
  const oneOff = slots.oneOff.length
    ? [t('loyalty.redeemSlots.summaryOneOff', { ns: 'loyalty', count: slots.oneOff.length })]
    : [];
  return [...weekly, ...oneOff].join(' · ');
};
